import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { grievancesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { GrievanceDetailButton } from '../../components/GrievanceDetailModal';

const OfficerGrievances = (): ReactElement => {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const navigate = useNavigate();

  // Debug function to check user and grievances
  const debugMinistryFiltering = async () => {
    try {
      const [userRes, grievancesRes] = await Promise.all([
        api.get('/grievances/debug/user'),
        api.get('/grievances/debug/grievances')
      ]);
      
      setDebugInfo({
        user: userRes.data.user,
        allGrievances: grievancesRes.data.allGrievances,
        filteredGrievances: items
      });
      
      console.log('=== Debug Info ===');
      console.log('User:', userRes.data.user);
      console.log('All Grievances:', grievancesRes.data.allGrievances);
      console.log('Filtered Grievances:', items);
      console.log('=== End Debug ===');
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await grievancesAPI.getAllSecured();
        setItems(res.data || []);
      } catch (e) {
        console.error('Error loading grievances:', e);
        toast.error('Failed to load grievances');
      } finally {
        setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 15000); // simple auto-refresh for near real-time
    return () => clearInterval(id);
  }, [user]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Ministry Grievances</h1>
        <button 
          onClick={debugMinistryFiltering}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Debug Filtering
        </button>
      </div>

      {/* Debug Info Display */}
      {debugInfo && (
        <div className="mb-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="font-bold mb-2">Debug Information:</h3>
          <div className="text-sm space-y-2">
            <div><strong>User Role:</strong> {debugInfo.user?.role}</div>
            <div><strong>User Ministry:</strong> {debugInfo.user?.ministry_name || 'NOT SET'}</div>
            <div><strong>Total Grievances:</strong> {debugInfo.allGrievances?.length}</div>
            <div><strong>Filtered Grievances:</strong> {debugInfo.filteredGrievances?.length}</div>
            <div><strong>Grievances with Ministry:</strong> {debugInfo.allGrievances?.filter((g: any) => g.ministry_name).length}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((g) => (
            <div key={g.id} className="bg-white rounded-lg shadow-md p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900 truncate">{g.subject || g.title}</span>
                </div>
                <div className="text-sm text-gray-600 truncate max-w-2xl">{g.description}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {g.ministry_name ? (
                    <span>Forwarded to: <span className="font-semibold text-indigo-600">{g.ministry_name}</span></span>
                  ) : (
                    <span>Not forwarded</span>
                  )}
                </div>
                <div className="mt-1 text-sm text-gray-800">
                  Grievance ID:{' '}
                  <button
                    type="button"
                    onClick={() => navigate(`/ministry/reviews?id=${encodeURIComponent(g.grievanceId)}`)}
                    className="font-mono font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                  >
                    <span className="font-mono font-bold text-red-600">{g.grievanceId}</span>
                  </button>
                  </div>
                <div className="mt-2 text-xs text-gray-500">Submitted by {g.fullName || g.submittedBy} • {new Date(g.createdAt).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  g.status === 'pending' ? 'text-yellow-700 bg-yellow-100' :
                  g.status === 'in-progress' ? 'text-blue-700 bg-blue-100' :
                  g.status === 'resolved' ? 'text-green-700 bg-green-100' :
                  'text-red-700 bg-red-100'
                }`}>
                  {g.status}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  g.priority === 'LOW' ? 'text-gray-700 bg-gray-100' :
                  g.priority === 'MEDIUM' ? 'text-blue-700 bg-blue-100' :
                  'text-red-700 bg-red-100'
                }`}>
                  {g.priority}
                </span>
                
                <GrievanceDetailButton grievanceId={g.grievanceId} />
              </div>
            </div>
          ))}
          
          {!loading && items.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p>No grievances assigned to your ministry yet.</p>
              <p className="text-sm mt-2">Grievances will appear here once they are forwarded to your ministry by the Super Admin.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfficerGrievances;


